import { useRef, useState } from "react";
import { getForm } from "../../../lib/forms/api";

// Structural copies of ChatLayout's form types (ground rule 5: no back-imports).
type FormQuestion = { id: string; prompt: string; kind?: string; pos: number; locked?: boolean };
type FormState = {
  questions: FormQuestion[];
  answers: Record<string, string>;
  answersByUser?: Record<string, Record<string, string>>;
  allSubmitted?: Record<string, boolean>;
  participants?: string[];
};

/**
 * Per-channel form (Questions) view state (step 5.2b): `formByChan` keyed by
 * fully-qualified channel id, plus its loaders. No inputs — loaders take an
 * fqChanId and use the imported `getForm` helper.
 *
 * `setFormByChan` + `scheduleFormReload` are returned for the parent socket
 * handlers (`onFormState`/`onFormAnswer`); `loadFormIfNeeded` for
 * onChannelList/switchChannel; `reloadForm` for the 5s form-refresh interval.
 *
 * Note: `formByChan` is not read at render time (FormChannelView fetches its own
 * data and only consumes channelTags). Its one live reader is loadFormIfNeeded's
 * dedup guard below, so it is NOT fully dead. Gutting the cache means removing the
 * onFormState/onFormAnswer handlers, which live in ChatLayout's deferred spine
 * socket effect — so that removal waits for step 7.3 (verified 2026-06-15).
 */
export function useFormData() {
  const [formByChan, setFormByChan] = useState<Record<string, FormState>>({});
  // Lightweight debounce for form reloads per channel
  const formReloadTimersRef = useRef<Record<string, number>>({});
  function scheduleFormReload(cid: string, delay = 250) {
    try {
      const timers = formReloadTimersRef.current;
      if (timers[cid]) { clearTimeout(timers[cid]); }
      timers[cid] = window.setTimeout(async () => {
        try { await reloadForm(cid); } finally { delete timers[cid]; }
      }, delay);
    } catch { /* noop */ }
  }

  async function loadFormIfNeeded(fqChanId: string) {
    try {
      if (formByChan[fqChanId]) return;
      const payload = await getForm(fqChanId);
      setFormByChan(old => ({
        ...old,
        [fqChanId]: {
          questions: payload.questions || [],
          answers: payload.myAnswers || {},
          answersByUser: payload.answersByUser || {},
          allSubmitted: payload.allSubmitted || {},
          participants: payload.participants || [],
        },
      }));
    } catch { /* noop */ }
  }
  async function reloadForm(fqChanId: string) {
    try {
      const payload = await getForm(fqChanId);
      setFormByChan(old => ({
        ...old,
        [fqChanId]: {
          questions: payload.questions || [],
          answers: payload.myAnswers || {},
          answersByUser: payload.answersByUser || {},
          allSubmitted: payload.allSubmitted || {},
          participants: payload.participants || [],
        },
      }));
    } catch { /* noop */ }
  }

  return {
    formByChan,
    setFormByChan,
    scheduleFormReload,
    loadFormIfNeeded,
    reloadForm,
  };
}
